import React, { useState } from "react";
import {
  Button,
  Col,
  Container,
  Input,
  Label,
  Row,
  FormGroup,
  FormFeedback,
  Badge,
} from "reactstrap";
import { useFormik } from "formik";
import * as Yup from "yup";
import { PageSubHeader } from "components/CommonLayouts/PageSubHeader";
import { CustomButton } from "components/CommonInputs/CustomButtons/CustomButton";
 
// Validation Schema
const validationSchema = Yup.object().shape({
  templateName: Yup.string().required("Template Name is required"),
  jobTitle: Yup.string().required("Job Title is required"),
  department: Yup.string().required("Department is required"),
  workType: Yup.string().required("Work Type is required"),
  jobLevel: Yup.string().required("Job Level is required"),
  experienceRequired: Yup.string().required("Experience is required"),
  payType: Yup.string().required("Pay Type is required"),
  immediateJoining: Yup.string().required("Immediate Joining is required"),
  salaryRange: Yup.string().required("Salary Range is required"),
  jobDescription: Yup.string().required("Job Description is required"),
  companyOverview: Yup.string().required("Company Overview is required"),
  rolesResponsibilities: Yup.string().required(
    "Roles & Responsibilities is required"
  ),
  requiredSkills: Yup.string().required("Required Skills is required"),
  preferredSkills: Yup.string().required("Preferred Skills is required"),
  educationalQualification: Yup.string().required(
    "Educational Qualification is required"
  ),
  companyUrl: Yup.string()
    .url("Must be a valid URL")
    .required("Company URL is required"),
  perksAndBenefits: Yup.string().required("Perks and Benefits are required"),
  saveAsTemplate: Yup.boolean(),
  templateNameForSave: Yup.string().when("saveAsTemplate", {
    is: true,
    then: (schema) => schema.required("Template Name for saving is required"),
    otherwise: (schema) => schema.notRequired(),
  }),
});

const NewTemplate = () => {
  const [interviewRounds, setInterviewRounds] = useState([]);
  const [requiredSkills, setRequiredSkills] = useState([]);
  const [preferredSkills, setPreferredSkills] = useState([]);

  // Handler for interview rounds checkboxes
  const handleInterviewRoundChange = (e, round) => {
    const checked = e.target.checked;
    if (checked) {
      setInterviewRounds((prev) => [...prev, round]);
    } else {
      setInterviewRounds((prev) => prev.filter((item) => item !== round));
    }
  };

  // Handlers for skill selection
  const handleSelect = (e, fieldName, setSkills, skills) => {
    const value = e.target.value;
    if (value && !skills.includes(value)) {
      const updatedSkills = [...skills, value];
      setSkills(updatedSkills);
      validation.setFieldValue(fieldName, updatedSkills.join(","));
    }
    e.target.value = "";
  };

  const removeSkill = (skillToRemove, fieldName, setSkills, skills) => {
    const updatedSkills = skills.filter((skill) => skill !== skillToRemove);
    setSkills(updatedSkills);
    validation.setFieldValue(fieldName, updatedSkills.join(","));
  };

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      templateName: "",
      jobTitle: "",
      department: "",
      workType: "",
      jobLevel: "",
      experienceRequired: "Fresher",
      payType: "",
      immediateJoining: "",
      salaryRange: "",
      jobDescription: "",
      companyOverview: "",
      rolesResponsibilities: "",
      requiredSkills: "",
      preferredSkills: "",
      educationalQualification: "",
      companyUrl: "",
      perksAndBenefits: "",
      saveAsTemplate: false,
      templateNameForSave: "",
    },
    validationSchema,
    onSubmit: (values, { setSubmitting }) => {
      const formData = {
        templateDetails: {
          createBy: "admin_user",
          templateName: values.templateName,
          jobTitle: values.jobTitle,
          department: values.department,
          workType: values.workType,
          jobLevel: values.jobLevel,
          experienceRequired: values.experienceRequired,
          payType: values.payType,
          immediateJoining: values.immediateJoining,
          salaryRange: values.salaryRange,
          jobDescription: values.jobDescription,
          companyOverview: values.companyOverview,
          rolesResponsibilities: values.rolesResponsibilities,
          requiredSkills: values.requiredSkills,
          preferredSkills: values.preferredSkills,
          educationalQualification: values.educationalQualification,
          companyUrl: values.companyUrl,
          perksAndBenefits: values.perksAndBenefits,
          interviewRounds: interviewRounds,
        },
      };
      // console.log("Form Data:", formData);
      setSubmitting(false);
    },
  });

  return (
    <div className="page-content">
      <PageSubHeader
        heading={"New Job Post"}
        routePath={"Home"}
        pageName={"Job Postings / New Job Post"}
      />
      <Container fluid className="py-4 px-5">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            validation.handleSubmit();
            return false;
          }}
        >
          {/* General Info */}
          <Row className="heading mt-3 pe-4">
            <p className="sub-head">01 - General</p>
          </Row>
          <Row>
            <Col md={4}>
              <FormGroup>
                <Label htmlFor="templateName">
                  Template Name <span className="text-danger">*</span>
                </Label>
                <Input
                  name="templateName"
                  id="templateName"
                  placeholder="Template Name"
                  onChange={validation.handleChange}
                  onBlur={validation.handleBlur}
                  value={validation.values.templateName}
                  invalid={
                    validation.touched.templateName &&
                    !!validation.errors.templateName
                  }
                />
                {validation.touched.templateName &&
                  validation.errors.templateName && (
                    <FormFeedback>
                      {validation.errors.templateName}
                    </FormFeedback>
                  )}
              </FormGroup>
            </Col>
            <Col md={4}>
              <FormGroup>
                <Label htmlFor="jobTitle">
                  Job Title/ Designation <span className="text-danger">*</span>
                </Label>
                <Input
                  name="jobTitle"
                  id="jobTitle"
                  placeholder="Job Title/ Designation"
                  onChange={validation.handleChange}
                  onBlur={validation.handleBlur}
                  value={validation.values.jobTitle}
                  invalid={
                    validation.touched.jobTitle && !!validation.errors.jobTitle
                  }
                />
                {validation.touched.jobTitle && validation.errors.jobTitle && (
                  <FormFeedback>{validation.errors.jobTitle}</FormFeedback>
                )}
              </FormGroup>
            </Col>
            <Col md={4}>
              <FormGroup>
                <Label htmlFor="department">
                  Department<span className="text-danger">*</span>
                </Label>
                <Input
                  type="select"
                  name="department"
                  id="department"
                  onChange={validation.handleChange}
                  onBlur={validation.handleBlur}
                  value={validation.values.department}
                  invalid={
                    validation.touched.department &&
                    !!validation.errors.department
                  }
                >
                  <option value="">Select</option>
                  <option>Engineering/ Development</option>
                  <option>Designer</option>
                  <option>Tester</option>
                </Input>
                {validation.touched.department &&
                  validation.errors.department && (
                    <FormFeedback>{validation.errors.department}</FormFeedback>
                  )}
              </FormGroup>
            </Col>
            <Col md={4}>
              <FormGroup>
                <Label htmlFor="workType">
                  Work Type <span className="text-danger">*</span>
                </Label>
                <Input
                  type="select"
                  name="workType"
                  id="workType"
                  onChange={validation.handleChange}
                  onBlur={validation.handleBlur}
                  value={validation.values.workType}
                  invalid={
                    validation.touched.workType && !!validation.errors.workType
                  }
                >
                  <option value="">Select</option>
                  <option>Full-Time</option>
                  <option>Part-Time</option>
                </Input>
                {validation.touched.workType && validation.errors.workType && (
                  <FormFeedback>{validation.errors.workType}</FormFeedback>
                )}
              </FormGroup>
            </Col>
            <Col md={4}>
              <FormGroup>
                <Label htmlFor="jobLevel">
                  Job Level <span className="text-danger">*</span>
                </Label>
                <Input
                  type="select"
                  name="jobLevel"
                  id="jobLevel"
                  onChange={validation.handleChange}
                  onBlur={validation.handleBlur}
                  value={validation.values.jobLevel}
                  invalid={
                    validation.touched.jobLevel && !!validation.errors.jobLevel
                  }
                >
                  <option value="">Select</option>
                  <option>Senior</option>
                  <option>Mid-level</option>
                </Input>
                {validation.touched.jobLevel && validation.errors.jobLevel && (
                  <FormFeedback>{validation.errors.jobLevel}</FormFeedback>
                )}
              </FormGroup>
            </Col>
            <Col md={4}>
              <FormGroup>
                <Label htmlFor="experienceRequired">
                  Experience <span className="text-danger">*</span>
                </Label>
                <Input
                  type="select"
                  name="experienceRequired"
                  id="experienceRequired"
                  onChange={validation.handleChange}
                  onBlur={validation.handleBlur}
                  value={validation.values.experienceRequired}
                  invalid={
                    validation.touched.experienceRequired &&
                    !!validation.errors.experienceRequired
                  }
                >
                  <option value="">Select</option>
                  <option>Fresher</option>
                  <option>5+ years</option>
                </Input>
                {validation.touched.experienceRequired &&
                  validation.errors.experienceRequired && (
                    <FormFeedback>
                      {validation.errors.experienceRequired}
                    </FormFeedback>
                  )}
              </FormGroup>
            </Col>
            <Col md={4}>
              <FormGroup>
                <Label htmlFor="payType">
                  Pay Type <span className="text-danger">*</span>
                </Label>
                <Input
                  type="select"
                  name="payType"
                  id="payType"
                  onChange={validation.handleChange}
                  onBlur={validation.handleBlur}
                  value={validation.values.payType}
                  invalid={
                    validation.touched.payType && !!validation.errors.payType
                  }
                >
                  <option value="">Select</option>
                  <option>Hourly</option>
                  <option>Monthly</option>
                  <option>Yearly</option>
                </Input>
                {validation.touched.payType && validation.errors.payType && (
                  <FormFeedback>{validation.errors.payType}</FormFeedback>
                )}
              </FormGroup>
            </Col>
            <Col md={4}>
              <FormGroup>
                <Label htmlFor="immediateJoining">
                  Immediate Joining? <span className="text-danger">*</span>
                </Label>
                <Input
                  type="select"
                  name="immediateJoining"
                  id="immediateJoining"
                  onChange={validation.handleChange}
                  onBlur={validation.handleBlur}
                  value={validation.values.immediateJoining}
                  invalid={
                    validation.touched.immediateJoining &&
                    !!validation.errors.immediateJoining
                  }
                >
                  <option value="">Select</option>
                  <option>Yes</option>
                  <option>No</option>
                </Input>
                {validation.touched.immediateJoining &&
                  validation.errors.immediateJoining && (
                    <FormFeedback>
                      {validation.errors.immediateJoining}
                    </FormFeedback>
                  )}
              </FormGroup>
            </Col>
            <Col md={4}>
              <FormGroup>
                <Label htmlFor="salaryRange">
                  Salary Range <span className="text-danger">*</span>
                </Label>
                <Input
                  name="salaryRange"
                  id="salaryRange"
                  placeholder="e.g., 100000-120000"
                  onChange={validation.handleChange}
                  onBlur={validation.handleBlur}
                  value={validation.values.salaryRange}
                  invalid={
                    validation.touched.salaryRange &&
                    !!validation.errors.salaryRange
                  }
                />
                {validation.touched.salaryRange &&
                  validation.errors.salaryRange && (
                    <FormFeedback>{validation.errors.salaryRange}</FormFeedback>
                  )}
              </FormGroup>
            </Col>
          </Row>

          {/* Job Description */}
          <Row className="heading mt-3 pe-4">
            <p className="sub-head">02 - Job Description</p>
          </Row>
          <Row>
            <Col>
              <FormGroup>
                <Label htmlFor="jobDescription">
                  Job Description <span className="text-danger">*</span>
                </Label>
                <Input
                  type="textarea"
                  name="jobDescription"
                  id="jobDescription"
                  placeholder="Type Something.."
                  rows={5}
                  onChange={validation.handleChange}
                  onBlur={validation.handleBlur}
                  value={validation.values.jobDescription}
                  invalid={
                    validation.touched.jobDescription &&
                    !!validation.errors.jobDescription
                  }
                />
                {validation.touched.jobDescription &&
                  validation.errors.jobDescription && (
                    <FormFeedback>
                      {validation.errors.jobDescription}
                    </FormFeedback>
                  )}
              </FormGroup>
            </Col>
          </Row>

          {/* Company Overview */}
          <Row className="heading mt-3 pe-4">
            <p className="sub-head">03 - Company Overview</p>
          </Row>
          <Row>
            <Col>
              <FormGroup>
                <Label htmlFor="companyOverview">
                  Company Overview <span className="text-danger">*</span>
                </Label>
                <Input
                  type="textarea"
                  name="companyOverview"
                  id="companyOverview"
                  placeholder="Type Something.."
                  rows={5}
                  onChange={validation.handleChange}
                  onBlur={validation.handleBlur}
                  value={validation.values.companyOverview}
                  invalid={
                    validation.touched.companyOverview &&
                    !!validation.errors.companyOverview
                  }
                />
                {validation.touched.companyOverview &&
                  validation.errors.companyOverview && (
                    <FormFeedback>
                      {validation.errors.companyOverview}
                    </FormFeedback>
                  )}
              </FormGroup>
              <FormGroup>
                <Label htmlFor="companyUrl">
                  Company URL <span className="text-danger">*</span>
                </Label>
                <Input
                  type="text"
                  name="companyUrl"
                  id="companyUrl"
                  placeholder="https://example.com"
                  onChange={validation.handleChange}
                  onBlur={validation.handleBlur}
                  value={validation.values.companyUrl}
                  invalid={
                    validation.touched.companyUrl &&
                    !!validation.errors.companyUrl
                  }
                />
                {validation.touched.companyUrl &&
                  validation.errors.companyUrl && (
                    <FormFeedback>{validation.errors.companyUrl}</FormFeedback>
                  )}
              </FormGroup>
            </Col>
          </Row>

          {/* Roles & Responsibilities */}
          <Row className="heading mt-3 pe-4">
            <p className="sub-head">04 - Roles & Responsibilities</p>
          </Row>
          <Row>
            <Col>
              <FormGroup>
                <Label htmlFor="rolesResponsibilities">
                  Roles & Responsibilities{" "}
                  <span className="text-danger">*</span>
                </Label>
                <Input
                  type="textarea"
                  name="rolesResponsibilities"
                  id="rolesResponsibilities"
                  placeholder="Type Something.."
                  rows={5}
                  onChange={validation.handleChange}
                  onBlur={validation.handleBlur}
                  value={validation.values.rolesResponsibilities}
                  invalid={
                    validation.touched.rolesResponsibilities &&
                    !!validation.errors.rolesResponsibilities
                  }
                />
                {validation.touched.rolesResponsibilities &&
                  validation.errors.rolesResponsibilities && (
                    <FormFeedback>
                      {validation.errors.rolesResponsibilities}
                    </FormFeedback>
                  )}
              </FormGroup>
            </Col>
          </Row>

          {/* Required Skills */}
          <Row className="heading mt-3 pe-4">
            <p className="sub-head">05 - Required Skills</p>
          </Row>
          <Row>
            <Col md={4}>
              <FormGroup>
                <Label htmlFor="requiredSkills">
                  Select Skills <span className="text-danger">*</span>
                </Label>
                <Input
                  type="select"
                  name="requiredSkills"
                  id="requiredSkills"
                  onChange={(e) =>
                    handleSelect(
                      e,
                      "requiredSkills",
                      setRequiredSkills,
                      requiredSkills
                    )
                  }
                  invalid={
                    validation.touched.requiredSkills &&
                    !!validation.errors.requiredSkills
                  }
                >
                  <option value="">Select</option>
                  <option value="Java">Java</option>
                  <option value="Python">Python</option>
                  <option value="JavaScript">JavaScript</option>
                </Input>
                {validation.touched.requiredSkills &&
                  validation.errors.requiredSkills && (
                    <FormFeedback>
                      {validation.errors.requiredSkills}
                    </FormFeedback>
                  )}
              </FormGroup>
              <div>
                {requiredSkills.map((skill) => (
                  <Badge key={skill} color="primary" className="me-1 mb-2">
                    {skill}
                    <Button
                      close
                      onClick={() =>
                        removeSkill(
                          skill,
                          "requiredSkills",
                          setRequiredSkills,
                          requiredSkills
                        )
                      }
                    />
                  </Badge>
                ))}
              </div>
            </Col>
            <Row>
              <Col>
                <FormGroup>
                  <Label htmlFor="requiredSkillsOther">Others (if any)</Label>
                  <Input
                    type="textarea"
                    name="requiredSkillsOther"
                    id="requiredSkillsOther"
                    placeholder="Type Something.."
                    rows={5}
                    onChange={validation.handleChange}
                    onBlur={validation.handleBlur}
                    value={validation.values.requiredSkillsOther}
                  />
                </FormGroup>
              </Col>
            </Row>
          </Row>

          {/* Preferred Skills */}
          <Row className="heading mt-3 pe-4">
            <p className="sub-head">06 - Preferred Skills</p>
          </Row>
          <Row>
            <Col md={4}>
              <FormGroup>
                <Label htmlFor="preferredSkills">
                  Select Skills <span className="text-danger">*</span>
                </Label>
                <Input
                  type="select"
                  name="preferredSkills"
                  id="preferredSkills"
                  onChange={(e) =>
                    handleSelect(
                      e,
                      "preferredSkills",
                      setPreferredSkills,
                      preferredSkills
                    )
                  }
                  invalid={
                    validation.touched.preferredSkills &&
                    !!validation.errors.preferredSkills
                  }
                >
                  <option value="">Select</option>
                  <option value="Java">Java</option>
                  <option value="Python">Python</option>
                  <option value="JavaScript">JavaScript</option>
                </Input>
                {validation.touched.preferredSkills &&
                  validation.errors.preferredSkills && (
                    <FormFeedback>
                      {validation.errors.preferredSkills}
                    </FormFeedback>
                  )}
              </FormGroup>
              <div>
                {preferredSkills.map((skill) => (
                  <Badge key={skill} color="primary" className="me-1 mb-2">
                    {skill}
                    <Button
                      close
                      onClick={() =>
                        removeSkill(
                          skill,
                          "preferredSkills",
                          setPreferredSkills,
                          preferredSkills
                        )
                      }
                    />
                  </Badge>
                ))}
              </div>
            </Col>
            <Row>
              <Col>
                <FormGroup>
                  <Label htmlFor="preferredSkillsOther">Others (if any)</Label>
                  <Input
                    type="textarea"
                    name="preferredSkillsOther"
                    id="preferredSkillsOther"
                    placeholder="Type Something.."
                    rows={5}
                    onChange={validation.handleChange}
                    onBlur={validation.handleBlur}
                    value={validation.values.preferredSkillsOther}
                  />
                </FormGroup>
              </Col>
            </Row>
          </Row>

          {/* Educational Qualification */}
          <Row className="heading mt-3 pe-4">
            <p className="sub-head">07 - Educational Qualification</p>
          </Row>
          <Row>
            <Col md={4}>
              <FormGroup>
                <Label htmlFor="educationalQualification">
                  Educational Qualification{" "}
                  <span className="text-danger">*</span>
                </Label>
                <Input
                  type="select"
                  name="educationalQualification"
                  id="educationalQualification"
                  onChange={validation.handleChange}
                  onBlur={validation.handleBlur}
                  value={validation.values.educationalQualification}
                  invalid={
                    validation.touched.educationalQualification &&
                    !!validation.errors.educationalQualification
                  }
                >
                  <option value="">Select</option>
                  <option>B.Tech / M.Tech in Computer Science</option>
                  <option>Master's Degree</option>
                  <option>Diploma</option>
                </Input>
                {validation.touched.educationalQualification &&
                  validation.errors.educationalQualification && (
                    <FormFeedback>
                      {validation.errors.educationalQualification}
                    </FormFeedback>
                  )}
              </FormGroup>
            </Col>
          </Row>

          {/* Perks and Benefits */}
          <Row className="heading mt-3 pe-4">
            <p className="sub-head">08 - Perks and Benefits</p>
          </Row>
          <Row>
            <Col>
              <FormGroup>
                <Label htmlFor="perksAndBenefits">
                  Perks and Benefits <span className="text-danger">*</span>
                </Label>
                <Input
                  type="textarea"
                  name="perksAndBenefits"
                  id="perksAndBenefits"
                  placeholder="Type Something.."
                  rows={5}
                  onChange={validation.handleChange}
                  onBlur={validation.handleBlur}
                  value={validation.values.perksAndBenefits}
                  invalid={
                    validation.touched.perksAndBenefits &&
                    !!validation.errors.perksAndBenefits
                  }
                />
                {validation.touched.perksAndBenefits &&
                  validation.errors.perksAndBenefits && (
                    <FormFeedback>
                      {validation.errors.perksAndBenefits}
                    </FormFeedback>
                  )}
              </FormGroup>
            </Col>
          </Row>

          {/* Interview Process */}
          <Row className="heading mt-3 pe-4">
            <p className="sub-head">09 - Interview Process</p>
          </Row>
          <Row>
            <p
              style={{
                color: "#222222",
                fontWeight: 700,
                fontSize: "14px",
                lineHeight: "20px",
                letterSpacing: "0%",
              }}
            >
              Select the interview rounds <span className="text-danger">*</span>
            </p>
            <Col>
              {[
                "HR Screening",
                "Technical Round 1",
                "Technical Round 2",
                "Managerial Round",
                "HR Discussion",
              ].map((round) => (
                <FormGroup
                  key={round}
                  className="d-flex gap-2 align-items-center"
                >
                  <Label
                    style={{
                      fontFamily: "Inter, sans-serif",
                      fontWeight: 500,
                      fontSize: "14px",
                      lineHeight: "22px",
                    }}
                  >
                    <Input
                      type="checkbox"
                      name="interviewRounds"
                      value={round}
                      onChange={(e) => handleInterviewRoundChange(e, round)}
                      onBlur={validation.handleBlur}
                      checked={interviewRounds.includes(round)}
                      className="me-2 border border-black"
                    />
                    {round}
                  </Label>
                </FormGroup>
              ))}
            </Col>
          </Row>

          {/* Save as Template */}
          <Row className="heading mt-3 pe-4">
            <p className="sub-head">10 - Save as Template</p>
          </Row>
          <Row>
            <Col>
              <FormGroup className="d-flex gap-2 align-items-center">
                <Label
                  htmlFor="saveAsTemplate"
                  style={{
                    fontFamily: "Inter, sans-serif",
                    fontWeight: 500,
                    fontSize: "14px",
                    lineHeight: "22px",
                  }}
                >
                  {" "}
                  <Input
                    type="checkbox"
                    name="saveAsTemplate"
                    id="saveAsTemplate"
                    onChange={validation.handleChange}
                    onBlur={validation.handleBlur}
                    checked={validation.values.saveAsTemplate}
                    className="me-2 border border-black"
                  />
                  Save this as a template
                </Label>
              </FormGroup>
              {validation.values.saveAsTemplate && (
                <Col md={4}>
                  <FormGroup>
                    <Label htmlFor="templateNameForSave">
                      Template Name <span className="text-danger">*</span>
                    </Label>
                    <Input
                      name="templateNameForSave"
                      id="templateNameForSave"
                      placeholder="Template Name"
                      onChange={validation.handleChange}
                      onBlur={validation.handleBlur}
                      value={validation.values.templateNameForSave}
                      invalid={
                        validation.touched.templateNameForSave &&
                        !!validation.errors.templateNameForSave
                      }
                    />
                    {validation.touched.templateNameForSave &&
                      validation.errors.templateNameForSave && (
                        <FormFeedback>
                          {validation.errors.templateNameForSave}
                        </FormFeedback>
                      )}
                  </FormGroup>
                </Col>
              )}
            </Col>
          </Row>

          {/* Buttons */}
          <Row className="mt-4">
            <Col className="gap-2 d-flex justify-content-end">
              <Button
                className="text-primary border-primary"
                style={{
                  backgroundColor: "#FFFFFF",
                  color: "#475467",
                  borderRadius: "6px",
                  border: "1px solid #475467",
                }}
              >
                Close
              </Button>
              <CustomButton text="Save Template" type="submit" />
            </Col>
          </Row>
        </form>
      </Container>
    </div>
  );
};
 
export default NewTemplate;